import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field, FormikProvider, useFormik } from "formik";
import moment from "moment";
import { Spin } from "antd";
import { Col, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomPaginationButtons from "../../../CustomControls/CustomPaginationButtons";
import CustomSelect from "../../../CustomControls/CustomSelect";
import CustomSizePerPage from "../../../CustomControls/CustomSizePerPage";
import { post } from "../../../helpers/api_helper";
import {
  POST_GetRawDataAdminReport,
  POST_GetRawDataReportFilters,
  POST_RawDataAdminReportExport,
} from "../../../helpers/url_helper";
import { errorMessage, warningMessage } from "../CommonFunctions";
import parameterText from "./TestingRawDataParameterText";

const paramKey = (type, idEncrypt) => `${type}_${idEncrypt}`;
const dateText = (value) => moment(value || new Date()).format("YYYY-MM-DD");

// Columns; every column has a fixed width (% of the table, adds up to 100)
const COLUMNS = [
  { title: "Entered On", width: "12%", render: (r) => r.EnteredOn },
  { title: "Equipment", width: "17%", render: (r) => r.EquipmentName },
  { title: "Entry Type", width: "10%", render: (r) => r.EntryType },
  { title: "TC No", width: "11%", render: (r) => r.TCNo, bold: true },
  { title: "Test", width: "12%", render: (r) => r.TestName },
  { title: "Parameter", width: "16%", render: parameterText },
  { title: "Reading", width: "5%", render: (r) => (r.ResultCount > 0 ? r.ResultCount : "") },
  { title: "Result Value", width: "8%", render: (r) => r.ResultValue },
  { title: "Employee", width: "9%", render: (r) => r.EnteredBy },
];

// Summary: the Detail grouped by TC + Test + Entry Type + Employee (no Parameter / Reading / Result Value)
const SUMMARY_COLUMNS = [
  { title: "Entered On", width: "12%", render: (r) => r.EnteredOn },
  { title: "Equipment", width: "18%", render: (r) => r.EquipmentName },
  { title: "Entry Type", width: "12%", render: (r) => r.EntryType },
  { title: "TC No", width: "12%", render: (r) => r.TCNo, bold: true },
  { title: "Test", width: "16%", render: (r) => r.TestName },
  { title: "Employee", width: "12%", render: (r) => r.EnteredBy },
  { title: "Entries", width: "6%", render: (r) => r.Entries },
  {
    title: "Status",
    width: "12%",
    render: (r) =>
      r.Status ? (
        <span className={r.Status === "Done" ? "text-success" : r.Status === "Partial" ? "text-warning" : "text-danger"}>
          {r.Status}
        </span>
      ) : (
        "-"
      ),
  },
];

// Detail = the log entries / pending / not mapped parameters, Summary = status per TC + Test
const REPORT_TYPE_OPTIONS = [
  { label: "Detail", value: "1" },
  { label: "Summary", value: "2" },
];

// which kind of entries to list (blank = all)
const ENTRY_TYPE_OPTIONS = [
  { label: "Raw Data", value: "1" },
  { label: "Equipment Usage", value: "2" },
  { label: "Pending Entry", value: "3" },
  { label: "Equipment Not Mapped", value: "4" },
];

// "i" icon after a label, a click shows the note
const noteLabel = (text, id, note) => (
  <>
    {text}{" "}
    <OverlayTrigger
      trigger="click"
      rootClose
      placement="bottom"
      overlay={
        <Popover id={id}>
          <Popover.Body>{note}</Popover.Body>
        </Popover>
      }
    >
      <span className="rdr-info" role="button" tabIndex={0} aria-label={`About ${text}`}>
        <i className="fa fa-info-circle" style={{ cursor: "pointer", color: "#9698ab" }}></i>
      </span>
    </OverlayTrigger>
  </>
);

const REPORT_TYPE_NOTE =
  "Detail lists every entry and parameter. Summary is the same data without the parameter details: one row per TC, Test, Entry Type and Employee, with the number of Detail rows it covers (Entries) and, for Raw Data and Pending Entry, the raw data status of the test: Pending (no parameter has a reading), Partial, Done (every parameter has a reading). Same date range, Entry Type and filters as Detail.";
const ENTRY_TYPE_NOTE =
  "Pending Entry rows are the allocated test parameters whose raw data entry is still pending (all employees), including tests whose Testing is already approved (shown as Pending Entry (Approved)). Equipment Not Mapped lists the allocated parameters that cannot be entered because no equipment is mapped; it is shown only when selected. Open Pending Entry rows and Equipment Not Mapped show the current position; Pending Entry (Approved) rows follow the date range (the day Testing was approved).";
const EMPLOYEE_NOTE = "The employee who entered the entry, or the employee a Pending Entry / Equipment Not Mapped test is allocated to.";

// What the From / To dates filter, for the selected Report Type + Entry Type (blank = nothing applies)
const dateNote = (entryType) => {
  if (entryType === "4") {
    return { applies: false, text: "Dates are not used for this Entry Type: it shows the current position of the allocated tests." };
  }
  if (entryType === "3") {
    return { applies: true, text: "Dates apply to the day Testing was approved (Pending Entry (Approved) rows). Open Pending Entry rows are always shown (current position)." };
  }
  if (entryType === "1") return { applies: true, text: "Dates apply to the day the reading was entered." };
  if (entryType === "2") return { applies: true, text: "Dates apply to the day the equipment usage was entered." };
  return { applies: true, text: "Dates apply to the day the reading / equipment usage was entered, and to the day Testing was approved (Pending Entry (Approved) rows). Open Pending Entry rows are always shown (current position)." };
};

const TestingRawDataReports = () => {
  const navigate = useNavigate();
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(10);
  const [searched, setSearched] = useState(false);
  const [loadedSummary, setLoadedSummary] = useState(false); // the rows on screen are Summary rows

  const formik = useFormik({
    initialValues: {
      ReportType: "1",
      FromDate: new Date(),
      ToDate: new Date(),
      EntryType: "",
      MachineIDEncrypt: "",
      EmployeeIDEncrypt: "",
      TestMasterIDEncrypt: "",
      ParameterKey: "",
      TCNo: "",
    },
  });
  const values = formik.values;

  const testOptions = useMemo(
    () =>
      (filters.ListOfTest ?? []).map((t) => ({
        // the same test name exists in several departments / test groups
        // Test - (Department) - [Test Group]
        label: `${t.TestName}${t.DepartmentName ? ` - (${t.DepartmentName})` : ""}${t.TestGroupName ? ` - [${t.TestGroupName}]` : ""}`,
        value: t.TestMasterIDEncrypt,
      })),
    [filters],
  );
  const parameterOptions = useMemo(
    () =>
      (filters.ListOfParameter ?? []).map((p) => ({
        label: p.ParameterName,
        value: paramKey(p.ParameterType, p.ParameterIDEncrypt),
      })),
    [filters],
  );
  const employeeOptions = useMemo(
    () => (filters.ListOfEmployee ?? []).map((u) => ({ label: u.EmployeeName, value: u.EmployeeIDEncrypt })),
    [filters],
  );
  const isSummary = values.ReportType === "2";
  const dateInfo = dateNote(values.EntryType);
  const machineOptions = useMemo(
    () =>
      (filters.ListOfEquipment ?? []).map((m) => ({
        // Equipment - [Machine Number], same as the equipment list of the entry page
        label: `${m.MachineName}${m.MachineNumber ? ` - [${m.MachineNumber}]` : ""}`,
        value: m.MachineIDEncrypt,
      })),
    [filters],
  );

  const payload = (page, size) => {
    const parameter = (filters.ListOfParameter ?? []).find(
      (p) => paramKey(p.ParameterType, p.ParameterIDEncrypt) === values.ParameterKey,
    );
    return {
      ViewType: isSummary ? 2 : 1,
      FromDate: dateText(values.FromDate),
      ToDate: dateText(values.ToDate),
      EntryType: Number(values.EntryType) || 0,
      MachineIDEncrypt: values.MachineIDEncrypt,
      EmployeeIDEncrypt: values.EmployeeIDEncrypt,
      TestMasterIDEncrypt: values.TestMasterIDEncrypt,
      ParameterType: parameter ? parameter.ParameterType : 0,
      ParameterIDEncrypt: parameter ? parameter.ParameterIDEncrypt : "",
      TCNo: values.TCNo,
      CurrentPage: page,
      PageSize: size,
      CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
    };
  };

  const validate = () => {
    if (dateInfo.applies && values.FromDate && values.ToDate && values.ToDate < values.FromDate) {
      warningMessage("To date cannot be before From date.");
      return false;
    }
    return true;
  };

  const getData = async (page = 1, sizePerPage = currentSizePerPage) => {
    if (!validate()) return;
    setIsLoading(true);
    await post(POST_GetRawDataAdminReport, payload(page, sizePerPage))
      .then((response) => {
        if (response?.IsSuccess) {
          const list = response?.ListOfRow ?? [];
          if (page > 1 && list.length === 0) {
            getData(page - 1, sizePerPage); // the last row of the last page is gone: step back one page
            return;
          }
          setRows(list);
          setLoadedSummary(isSummary);
          setRecordCount(response?.TotalRecords ?? 0);
          setCurrentPage(page);
          setCurrentSizePerPage(sizePerPage);
          setSearched(true);
        } else {
          setRows([]);
          setRecordCount(0);
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const exportReport = async () => {
    if (!validate()) return;
    setIsLoading(true);
    await post(POST_RawDataAdminReportExport, payload(0, 0))
      .then((response) => {
        if (response?.IsSuccess) {
          window.open(response?.FilePath);
        } else {
          errorMessage(response?.Message);
        }
      })
      .catch((error) => console.log(error));
    setIsLoading(false);
  };

  // the To date can never be before the From date
  useEffect(() => {
    if (values.ToDate && values.FromDate && values.ToDate < values.FromDate) {
      formik.setFieldValue("ToDate", values.FromDate);
    }
  }, [values.FromDate]);

  // opens with today's entries
  useEffect(() => {
    getData(1, currentSizePerPage);
  }, []);

  // switching Detail <-> Summary: the old rows have the other layout, so clear them and load the new report right away
  const reportTypeChanged = useRef(false);
  useEffect(() => {
    if (!reportTypeChanged.current) {
      reportTypeChanged.current = true; // first render: the initial load above does the work
      return;
    }
    setRows([]);
    setRecordCount(0);
    setCurrentPage(1);
    setLoadedSummary(isSummary);
    getData(1, currentSizePerPage);
  }, [values.ReportType]);

  // values of the filter drop-downs: everything that has been logged in the branch
  useEffect(() => {
    post(POST_GetRawDataReportFilters, {
      CompanyIDEncrypt: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypt: AuthUser?.BranchIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setFilters(response);
        } else {
          errorMessage(response?.Message);
        }
      })
      .catch((error) => console.log(error));
  }, []);

  return (
    <div className="card m-1 raw-data-reports">
      <div className="card-body p-1">
        <div className="tbl-caption p-2">
          <h4 className="heading mb-0">Raw Data - Log book</h4>
          <button
            type="button"
            title="Go to Reports"
            className="btn btn-outline-primary btn-sm"
            onClick={() => navigate("/reports")}
          >
            <i className="fa-solid fa-table" /> Back to Report Menu
          </button>
        </div>
        <hr className="mt-0 mb-1"></hr>
        <div className="p-2 pt-0 pb-0">
          <Spin spinning={isLoading} tip="Loading...">
            <FormikProvider value={formik}>
              <Row className="me-0 mt-2">
                <Col xs={12} md={6} lg={3}>
                  <Field
                    label={noteLabel("Report Type", "rdr-report-type-info", REPORT_TYPE_NOTE)}
                    name="ReportType"
                    placeholder={"Detail"}
                    options={REPORT_TYPE_OPTIONS}
                    component={CustomSelect}
                    isClearable={false}
                    menuPosition="fixed"
                  />
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <div className={`d-flex ${dateInfo.applies ? "" : "rdr-date-off"}`}>
                    <Field
                      label="From Date"
                      name="FromDate"
                      placeholder={"Select From Date"}
                      component={CustomFlatpickr}
                      options={{ altInput: true, altFormat: "j-M-Y", dateFormat: "d-m-Y" }}
                    />
                    <div>&nbsp;</div>
                    <Field
                      label={noteLabel("To Date", "rdr-date-info", dateInfo.text)}
                      name="ToDate"
                      placeholder={"Select To Date"}
                      component={CustomFlatpickr}
                      options={{ altInput: true, altFormat: "j-M-Y", dateFormat: "d-m-Y", minDate: values.FromDate }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Field
                    label={noteLabel("Entry Type", "rdr-entry-info", ENTRY_TYPE_NOTE)}
                    name="EntryType"
                    placeholder={"All Entry Types"}
                    options={ENTRY_TYPE_OPTIONS}
                    component={CustomSelect}
                    menuPosition="fixed"
                  />
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Field
                    label="Equipment"
                    name="MachineIDEncrypt"
                    placeholder={"All Equipment"}
                    options={machineOptions}
                    component={CustomSelect}
                    menuPosition="fixed"
                  />
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Field
                    label={noteLabel("Employee", "rdr-user-info", EMPLOYEE_NOTE)}
                    name="EmployeeIDEncrypt"
                    placeholder={"All Employees"}
                    options={employeeOptions}
                    component={CustomSelect}
                    menuPosition="fixed"
                  />
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Field label="TC No" name="TCNo" placeholder={"Search here..."} component={CustomInput} />
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Field
                    label="Test"
                    name="TestMasterIDEncrypt"
                    placeholder={"Select Test"}
                    options={testOptions}
                    component={CustomSelect}
                    menuPosition="fixed"
                  />
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Field
                    label="Parameter"
                    name="ParameterKey"
                    placeholder={"Select Parameter"}
                    options={parameterOptions}
                    component={CustomSelect}
                    menuPosition="fixed"
                  />
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <label className="mb-1 d-block">&nbsp;</label>
                  <div className="rdr-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm me-2 mb-1"
                      onClick={() => getData(1, currentSizePerPage)}
                    >
                      <i className="fa fa-search" aria-hidden="true"></i> Search
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mb-1"
                      onClick={exportReport}
                      disabled={isLoading || recordCount === 0}
                    >
                      <i className="fa-solid fa-file-excel me-2"></i>Export Excel
                    </button>
                  </div>
                </Col>
              </Row>
            </FormikProvider>

            <div className="rdr-table table-responsive active-projects style-1 shorting">
              <div className="dataTables_wrapper no-footer">
                <table className="table dataTable no-footer mb-0">
                  <thead>
                    <tr>
                      {(loadedSummary ? SUMMARY_COLUMNS : COLUMNS).map((c) => (
                        <th key={c.title} style={{ textAlign: "left", width: c.width }}>
                          {c.title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length > 0 ? (
                      rows.map((row, i) => (
                        <tr key={i}>
                          {(loadedSummary ? SUMMARY_COLUMNS : COLUMNS).map((c) => (
                            <td
                              key={c.title}
                              className={c.bold ? "fw-bold" : undefined}
                              style={{ textAlign: "left", verticalAlign: "middle" }}
                            >
                              {c.render(row)}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={(loadedSummary ? SUMMARY_COLUMNS : COLUMNS).length} className="text-center text-muted">
                          {isLoading ? "" : searched ? "No record found." : ""}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {(values.EntryType === "" || values.EntryType === "3") && (
                  <div className="small text-muted py-1">
                    Pending parameters that have no equipment mapped are not listed here. Select Entry Type "Equipment Not Mapped" to see them.
                  </div>
                )}
                <div className="d-sm-flex text-center justify-content-between align-items-center">
                  <CustomSizePerPage getData={getData} currentSizePerPage={currentSizePerPage} />
                  <div className="dataTables_info">
                    Showing {recordCount > 0 ? (currentPage - 1) * currentSizePerPage + 1 : 0} to{" "}
                    {recordCount > currentPage * currentSizePerPage ? currentPage * currentSizePerPage : recordCount} of{" "}
                    {recordCount} entries
                  </div>
                  <CustomPaginationButtons
                    totalPages={Math.ceil(recordCount / currentSizePerPage)}
                    currentPage={currentPage}
                    currentSizePerPage={currentSizePerPage}
                    getData={getData}
                  />
                </div>
              </div>
            </div>
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default TestingRawDataReports;
